import { useLocation, useNavigate } from "react-router-dom";
import { brl } from "@/lib/api";
import { Contact, ScanBarcode, X } from "lucide-react";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { Button, DataTable, PageHeader, SearchInput } from "@/components/ui-kit";

/**
 * A mesma tela é montada nos três painéis (admin, vendedor e balcão) e duas
 * coisas mudam junto com a rota (panel/menus.js):
 *  - o PDV: no admin é /admin/pdv, nos outros dois é a própria raiz do painel;
 *  - a seção do menu que abriga a tela, que é o que o eyebrow anuncia — deixá-lo
 *    fixo em "Balcão" faria o vendedor ler uma seção que não existe na barra
 *    lateral dele. Mesma decisão de seller/Commissions.js.
 */
const PANEL = {
  "/admin": { pdv: "/admin/pdv", section: "Balcão" },
  "/vendedor": { pdv: "/vendedor", section: "Gestão" },
  "/balcao": { pdv: "/balcao", section: "Operação" },
};

export default function SellerCustomers() {
  // O agrupamento por cliente passou a ser feito no banco; a busca vai junto.
  const {
    items: filtered, total, page, lastPage, setPage, search, setSearch,
    isLoading: loading, isError: error, refetch: retry,
  } = usePaginatedList("/pos/customers");

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const base = `/${pathname.split("/")[1]}`;
  const { pdv: pdvPath, section } = PANEL[base] || PANEL["/admin"];

  return (
    <div className="space-y-5" data-testid="seller-customers-page">
      <PageHeader
        eyebrow={section}
        title="Meus clientes atendidos"
        icon={Contact}
        description="Vendas do PDV agrupadas por cliente, só as feitas com o seu login. Mostra quanto cada um já gastou e há quanto tempo não volta — é por aqui que se identifica quem sumiu."
        actions={(
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome..." data-testid="seller-customers-search" aria-label="Buscar cliente atendido por nome" width="w-60" />
        )}
      />

      <DataTable
        rowTestId="seller-customer-row"
        caption="Clientes atendidos por você no balcão"
        rows={filtered}
        // O servidor agrupa por customer_name, então o nome é chave única na
        // listagem — melhor que o índice, que remontava a linha a cada página.
        rowKey={(c) => c.name}
        loading={loading}
        error={error}
        onRetry={retry}
        empty={{
          icon: Contact,
          title: search ? "Nenhum cliente encontrado" : "Nenhum cliente atendido ainda",
          desc: search
            ? "Nenhum atendimento seu bate com esse nome. A busca só enxerga vendas feitas com o seu login."
            : "Os clientes aparecem aqui depois da primeira venda registrada no PDV com o seu login.",
          action: search
            ? <Button variant="secondary" icon={X} onClick={() => setSearch("")} data-testid="clear-search-btn">Limpar busca</Button>
            : <Button icon={ScanBarcode} onClick={() => navigate(pdvPath)}>Abrir o PDV</Button>,
          testId: "seller-customers-empty",
        }}
        columns={[
          {
            key: "cliente", header: "Cliente", className: "font-medium text-slate-900",
            cell: (c) => c.name,
          },
          { key: "documento", header: "CPF/CNPJ", mono: true, className: "text-slate-500", cell: (c) => c.document || "—" },
          { key: "compras", header: "Compras", align: "center", mono: true, cell: (c) => c.purchases },
          { key: "total", header: "Total", align: "right", mono: true, className: "font-semibold", cell: (c) => brl(c.total) },
          {
            key: "ultima", header: "Última compra", className: "text-slate-500",
            cell: (c) => new Date(c.last_purchase).toLocaleDateString("pt-BR"),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="clientes" />
    </div>
  );
}
